import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Instagram, ChevronLeft, ChevronRight, Play, MoveHorizontal } from "lucide-react";

const IG_URL = "https://www.instagram.com/holdstii.in";

// Fallback tiles: portrait (4:5) frames from the Holdstill showreel, shown
// until the team curates post/reel links in CRM > Settings.
const FALLBACK_TILES = [
  { src: "/ig/portrait-6.jpg", alt: "Holdstill Records — showreel still" },
  { src: "/ig/portrait-14.jpg", alt: "Holdstill Records — music video production" },
  { src: "/ig/portrait-21.jpg", alt: "Holdstill Records — live session" },
  { src: "/ig/portrait-29.jpg", alt: "Holdstill Records — on set" },
  { src: "/ig/portrait-37.jpg", alt: "Holdstill Records — live performance" },
  { src: "/ig/portrait-45.jpg", alt: "Holdstill Records — stage shot" },
];

// Cinematic carousel: cards sit like scattered film frames (staggered +
// slightly rotated), the card nearest the center scales up bright with a lime
// glow while the rest recede, edges fade to black, and the whole strip slowly
// auto-drifts until the visitor takes over (drag / swipe / wheel / arrows).
export const InstagramShowcase = () => {
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const trackRef = useRef(null);
  const autoState = useRef({ dir: 1, hold: false });
  const idleTimer = useRef(null);
  const dragState = useRef({ down: false, startX: 0, startScroll: 0 });

  useEffect(() => {
    api
      .get("/instagram/posts")
      .then(({ data }) => setPosts(data.items || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const hasCurated = posts && posts.length > 0;

  // Center-focus effect: scale/brightness/glow follow distance from center
  const applyFocus = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    track.querySelectorAll("[data-card]").forEach((card) => {
      const r = card.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - center) / rect.width;
      const scale = 1.04 - Math.min(d * 0.36, 0.16);
      const bright = 1.04 - Math.min(d * 0.85, 0.48);
      const baseRot = parseFloat(card.dataset.rot) || 0;
      const rot = baseRot * Math.min(d * 2.4, 1);
      card.style.transform = `perspective(1000px) rotate(${rot}deg) scale(${scale})`;
      card.style.filter = `brightness(${bright})`;
      const glow = card.querySelector("[data-glow]");
      if (glow) glow.style.opacity = String(Math.max(0, 1 - d * 4.5));
    });
    const max = track.scrollWidth - track.clientWidth;
    setProgress(max > 0 ? track.scrollLeft / max : 0);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || loading) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyFocus);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // initial focus pass (after layout)
    const t = setTimeout(applyFocus, 100);
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [applyFocus, loading, posts]);

  // Gentle auto-drift, bouncing between ends; pauses while the user engages.
  // Uses a float accumulator because scrollLeft assignment rounds to integers
  // (fractional increments would otherwise never move the strip).
  useEffect(() => {
    const track = trackRef.current;
    if (!track || loading) return;
    let raf;
    const step = () => {
      const s = autoState.current;
      if (!s.hold && !document.hidden && !dragState.current.down) {
        const max = track.scrollWidth - track.clientWidth;
        if (max > 4) {
          if (s.pos === undefined || Math.abs(track.scrollLeft - s.pos) > 2) {
            s.pos = track.scrollLeft; // resync after user scroll
          }
          s.pos = Math.min(Math.max(s.pos + 0.45 * s.dir, 0), max);
          track.scrollLeft = s.pos;
          if (s.pos >= max - 1) s.dir = -1;
          else if (s.pos <= 1) s.dir = 1;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [loading, posts]);

  const holdAuto = useCallback(() => {
    autoState.current.hold = true;
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      autoState.current.hold = false;
    }, 4000);
  }, []);

  // Desktop drag-to-scroll
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") {
      holdAuto();
      return;
    }
    const track = trackRef.current;
    dragState.current = { down: true, startX: e.clientX, startScroll: track.scrollLeft };
    track.classList.add("dragging");
    holdAuto();
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragState.current;
      if (!d.down || !trackRef.current) return;
      trackRef.current.scrollLeft = d.startScroll - (e.clientX - d.startX);
    };
    const onUp = () => {
      dragState.current.down = false;
      trackRef.current?.classList.remove("dragging");
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const scrollByCards = (dir) => {
    holdAuto();
    trackRef.current?.scrollBy({ left: dir * 372, behavior: "smooth" });
  };

  return (
    <section className="relative py-14 sm:py-20 overflow-hidden" data-testid="instagram-section">
      {/* Cinematic backdrop: giant outline handle + lime wash behind the strip */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <span className="outline-text font-display uppercase whitespace-nowrap text-[14vw] opacity-40 select-none">
          @HOLDSTII.IN
        </span>
      </div>
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[50%] rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,rgba(202,255,26,0.07),transparent_70%)] blur-2xl"
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10"
        >
          <div>
            <p className="font-mono-ui text-[11px] tracking-[0.35em] uppercase text-[color:var(--hs-ink-dim)]">
              Live from the scene
            </p>
            <h2 className="mt-3 font-display font-display-rough uppercase text-3xl sm:text-4xl lg:text-5xl text-white">
              The <span className="text-[color:var(--hs-lime)]">Feed</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <a href={IG_URL} target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-cta">
              <Button
                variant="outline"
                className="btn-lift bg-transparent border-white/20 text-white hover:border-[color:rgba(202,255,26,0.55)] hover:bg-[color:rgba(202,255,26,0.06)] hover:text-white rounded-xl h-11 px-5"
              >
                <Instagram className="h-4 w-4 mr-2" />
                Follow @holdstii.in
              </Button>
            </a>
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => scrollByCards(-1)}
                data-testid="instagram-carousel-prev"
                aria-label="Previous posts"
                className="h-11 w-11 rounded-full border border-white/20 bg-black/30 flex items-center justify-center text-white hover:border-[color:var(--hs-lime)] hover:text-[color:var(--hs-lime)] btn-lift"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => scrollByCards(1)}
                data-testid="instagram-carousel-next"
                aria-label="Next posts"
                className="h-11 w-11 rounded-full border border-white/20 bg-black/30 flex items-center justify-center text-white hover:border-[color:var(--hs-lime)] hover:text-[color:var(--hs-lime)] btn-lift"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {loading ? (
        <div className="flex gap-5 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="w-[280px] sm:w-[330px] shrink-0 aspect-[4/5] rounded-2xl bg-white/5" />
          ))}
        </div>
      ) : (
        <div className="relative">
          <div
            ref={trackRef}
            onPointerDown={onPointerDown}
            onWheel={holdAuto}
            onTouchStart={holdAuto}
            className="carousel-track carousel-fade no-scrollbar flex items-start gap-5 sm:gap-7 overflow-x-auto px-[12vw] sm:px-[18vw] pt-4 pb-12"
            data-testid="instagram-carousel"
          >
            {(hasCurated ? posts : FALLBACK_TILES).map((item, i) => (
              <motion.div
                key={hasCurated ? item.id : item.src}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                className="shrink-0"
                style={{ marginTop: i % 2 ? "2.5rem" : "0rem" }}
              >
                <div
                  data-card
                  data-rot={i % 2 ? "2.2" : "-2.2"}
                  data-testid={`instagram-card-${i}`}
                  className="relative w-[280px] sm:w-[330px] transform-gpu will-change-transform"
                >
                  {/* lime glow for the in-focus card */}
                  <div
                    data-glow
                    className="absolute -inset-4 rounded-[1.8rem] bg-[radial-gradient(60%_60%_at_50%_50%,rgba(202,255,26,0.3),transparent_70%)] blur-xl opacity-0 pointer-events-none"
                    aria-hidden="true"
                  />
                  <div className="relative rounded-2xl border border-white/12 bg-[#0f1114] overflow-hidden shadow-[var(--shadow-elev-2)]">
                    <div className="flex items-center justify-between px-3.5 h-10 border-b border-white/10 bg-black/40">
                      <span className="flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-white/60">
                        {hasCurated && item.kind === "reel" ? (
                          <Play className="h-3.5 w-3.5 text-[color:var(--hs-lime)]" />
                        ) : (
                          <Instagram className="h-3.5 w-3.5 text-[color:var(--hs-lime)]" />
                        )}
                        {String(i + 1).padStart(2, "0")} · {hasCurated ? item.kind : "holdstill"}
                      </span>
                      <a
                        href={hasCurated ? item.url : IG_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-[color:var(--hs-lime)]"
                      >
                        Open ↗
                      </a>
                    </div>
                    <div className="relative aspect-[4/5] bg-black">
                      {hasCurated ? (
                        <iframe
                          src={item.embed_url}
                          title={item.label || `Instagram ${item.kind}`}
                          className="absolute inset-0 w-full h-full"
                          frameBorder="0"
                          scrolling="no"
                          loading="lazy"
                          allow="encrypted-media"
                        />
                      ) : (
                        <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="ig-tile block h-full">
                          <img src={item.src} alt={item.alt} className="w-full h-full object-cover" loading="lazy" />
                          <div className="ig-overlay absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-2">
                            <Instagram className="h-7 w-7 text-[color:var(--hs-lime)]" />
                            <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/90">
                              @holdstii.in
                            </span>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Progress + drag hint */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 flex items-center gap-4">
            <div className="h-[3px] flex-1 rounded-full bg-white/10 overflow-hidden" data-testid="instagram-carousel-progress">
              <div
                className="h-full rounded-full bg-[color:var(--hs-lime)] transition-[width] duration-150"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <span className="hidden sm:flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/35 shrink-0">
              <MoveHorizontal className="h-3.5 w-3.5 text-[color:var(--hs-lime)]" /> Drag to explore
            </span>
          </div>
        </div>
      )}
    </section>
  );
};
